// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { ProductCoDescriptor } from './api.js';

/**
 * Bluejade sibling-brand mechanism (COB-394).
 *
 * ProductCo — the yield-engine counterparty on the other side of every
 * offer — carries a distinct sibling brand, "Bluejade Markets · A Cobault
 * strategy provider" (the agreed "Sidecar 01" treatment in the
 * claude.ai/design system), applied ONLY to offer presentation +
 * contract/terms surfaces. Everything else (nav, global header, vault
 * list, shared vaults, onboarding) stays Cobault.
 *
 * Principle: identity and copy arrive from the server as *data* (the
 * optional `productco` descriptor on the offer / terms responses); the
 * *visual treatment* (accent scope + mark) ships in the bundle, keyed by
 * the descriptor's stable `id`. A server can rename the brand or reword
 * the lockup without a redeploy; it cannot invent a colourway or a mark —
 * an unknown id renders the server copy on stock Cobault tokens, never an
 * unstyled guess.
 *
 * SHIPS INERT: with no descriptor (today's reality — no backend sends
 * `productco` yet) `resolveBrand` returns the legacy label, no brand attr,
 * and no lockup, so every consuming surface renders byte-identical to
 * pre-COB-394.
 */

/** Re-export so treatment consumers can pull the descriptor type + the
 *  brand resolver from one module. `ProductCoDescriptor` itself lives in
 *  `api.ts` with the other wire DTOs (it is the wire source of truth). */
export type { ProductCoDescriptor } from './api.js';

/** The legacy, single-product label. Rendered whenever no descriptor is
 *  present — the exact string every offer surface showed pre-COB-394. */
export const LEGACY_PRODUCT_LABEL = 'BTC yield by Cobault ProductCo';

/** A resolved brand treatment a surface renders from. */
export interface Brand {
  /** `data-brand` attribute value that scopes the CSS accent override.
   *  `undefined` on the legacy path and for unknown ids (stock Cobault
   *  tokens); a consuming JSX `data-brand={attr}` then omits the attribute
   *  entirely — the inert property. */
  attr?: string;
  /** Self-hosted brand mark path (strict CSP — no external refs).
   *  `undefined` unless a known id supplies one. */
  markSrc?: string;
  /** Substring of the display name rendered in the accent colour — the
   *  two-tone wordmark treatment ("Blue" + *jade* + " Markets" per the
   *  agreed brand sheet). Bundle-side, known ids only; when the server
   *  name doesn't contain the run, the wordmark renders plain. */
  accentRun?: string;
  /** Server-provided display name. `undefined` on the legacy path. */
  displayName?: string;
  /** Server-provided endorsement line, e.g. "A Cobault strategy provider".
   *  `undefined` when the descriptor omits it (or on the legacy path). */
  lockup?: string;
  /** Product-type label (the "Type" row / offer-card title). Always set —
   *  the legacy string when absent, derived from the display name
   *  otherwise. */
  productLabel: string;
}

/**
 * id → visual treatment registry. The ONLY place a counterparty brand's
 * bundle-side treatment is declared. Copy and identity never live here —
 * they arrive as data on the descriptor. Adding a brand = one row here + a
 * scope in `styles/brand-<id>.css` + a self-hosted mark under `public/`.
 *
 * `bluejade` is the agreed "Sidecar 01 · Bluejade Markets" treatment from
 * the claude.ai/design system (brand-sheet-bluejade + vault-card sheets):
 * the Cut mark, jade accent scope, and the two-tone wordmark accent run.
 */
const BRAND_TREATMENTS: Record<
  string,
  { attr: string; markSrc: string; accentRun?: string }
> = {
  bluejade: { attr: 'bluejade', markSrc: '/bluejade-mark.svg', accentRun: 'jade' },
};

/**
 * Resolve a counterparty descriptor to the treatment a surface renders.
 * Three paths, in order:
 *  1. Absent descriptor → legacy label, no attr, no mark, no lockup
 *     (byte-identical to pre-COB-394 — the inert path).
 *  2. Known id → accent scope + mark from the registry, copy from the
 *     server descriptor, product label derived from the display name.
 *  3. Unknown id → server copy (display name / lockup / derived label) on
 *     stock Cobault tokens (no attr, no mark).
 */
export function resolveBrand(descriptor?: ProductCoDescriptor): Brand {
  if (!descriptor) {
    return { productLabel: LEGACY_PRODUCT_LABEL };
  }
  const { id, display_name, lockup } = descriptor;
  const productLabel = `BTC yield by ${display_name}`;
  const treatment = BRAND_TREATMENTS[id];
  if (!treatment) {
    // Unknown id: honour the server copy, but never guess a colourway or
    // a mark — stock Cobault tokens (no attr).
    return { displayName: display_name, lockup, productLabel };
  }
  return {
    attr: treatment.attr,
    markSrc: treatment.markSrc,
    accentRun: treatment.accentRun,
    displayName: display_name,
    lockup,
    productLabel,
  };
}
